import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import uniq from 'lodash.uniq';
import useAxios from 'axios-hooks';

import { Button, SelectRaw } from '../../../../components';

const ReferralModal = ({ isOpen, onClose, onReferVisit, patientId }) => {
	const [specialty, setSpecialty] = useState('');
	const [practiceId, setPracticeId] = useState(0);
	const [providerId, setProviderId] = useState(0);
	const [referralReason, setReferralReason] = useState('');
	const [{ data, loading: loadingPractices /* error */ }, loadPractices] =
		useAxios(
			{
				method: 'GET',
				url: `/patients/${patientId}/available-referral-practices`,
			},
			{ manual: true }
		);

	useEffect(() => {
		if (isOpen) {
			loadPractices();
		} else {
			setSpecialty('');
			setPracticeId(0);
			setProviderId(0);
			setReferralReason('');
		}
	}, [isOpen]);

	let referralPractices = [];
	let specialties = [];
	const practices = [];
	let providers = [];
	if (Array.isArray(data?.cb)) {
		referralPractices = data.cb;
		referralPractices.forEach((practice) => {
			try {
				const specialties = JSON.parse(practice.specialties);
				practice.specialties = specialties.specialties;
			} catch (err) {
				// TODO?
			}
		});
		specialties = uniq(
			referralPractices
				.map(({ specialties }) => specialties)
				.reduce((prev, curr) => {
					prev.push(...curr);
					return prev;
				}, [])
		).sort();
	}
	if (specialty !== '') {
		referralPractices.forEach((practice) => {
			if (practice.specialties.includes(specialty)) {
				if (
					!practices.find(
						(p) => p.provider_practice_id === practice.provider_practice_id
					)
				) {
					practices.push(practice);
				}
			}
		});
	}
	if (practiceId != 0) {
		providers = referralPractices.filter(
			({ provider_practice_id }) => provider_practice_id == practiceId
		);
	}

	const handleSpecialtyChosen = ({ target }) => {
		setSpecialty(target.value);
		setPracticeId(0);
		setProviderId(0);
	};

	const handlePracticeChosen = ({ target }) => {
		setPracticeId(target.value);
		setProviderId(0);
	};

	const handleReferVisit = () => {
		onReferVisit({ specialty, practiceId, providerId, referralReason });
	};

	return (
		<Modal
			isOpen={isOpen}
			contentLabel="Refer Patient"
			className="modal-dialog"
		>
			<div className="modal-content">
				<div className="modal-header">
					<div className="clearfix">
						<div className="pull-right">
							<button className="btn btn-link" onClick={onClose}>
								<em className="fa fa-lg fa-times" />
							</button>
						</div>
						<h3 className="modal-title">Refer Patient</h3>
					</div>
				</div>
				<div className="modal-body form-horizontal">
					<div className="form-group">
						<label className="col-lg-4 control-label">Specialty</label>
						<div className="col-lg-8">
							<SelectRaw
								selectedValue={specialty}
								options={[{ label: 'Choose...', value: '' }, ...specialties]}
								onChange={handleSpecialtyChosen}
							/>
						</div>
					</div>
					<div className="form-group">
						<label className="col-lg-4 control-label">Practice</label>
						<div className="col-lg-8">
							<SelectRaw
								selectedValue={practiceId}
								options={[
									{ label: 'Choose...', value: 0 },
									...practices.map(
										({ provider_practice_id, practice_name }) => ({
											value: provider_practice_id,
											label: practice_name,
										})
									),
								]}
								onChange={handlePracticeChosen}
								disabled={specialty === ''}
							/>
						</div>
					</div>
					<div className="form-group">
						<label className="col-lg-4 control-label">Provider</label>
						<div className="col-lg-8">
							<SelectRaw
								selectedValue={providerId}
								options={[
									{ label: 'Choose...', value: 0 },
									...providers.map((provider) => ({
										value: provider.provider_id,
										label: `${provider.name_first} ${provider.name_last}, ${provider.credentials}`,
									})),
								]}
								onChange={({ target }) => setProviderId(target.value)}
								disabled={practiceId === 0}
							/>
						</div>
					</div>
					<div className="form-group">
						<label className="col-lg-4 control-label">Referral Reason</label>
						<div className="col-lg-8">
							<textarea
								className="form-control referral-reason"
								value={referralReason}
								onChange={({ target }) => setReferralReason(target.value)}
							/>
						</div>
					</div>
				</div>
				<div className="modal-footer">
					<Button title="Cancel" large onClick={onClose} />
					<Button
						title="End Visit"
						onClick={handleReferVisit}
						large
						primary
						className="ml"
						disabled={loadingPractices || referralPractices.length === 0}
					/>
				</div>
			</div>
		</Modal>
	);
};

ReferralModal.propTypes = {
	isOpen: PropTypes.bool,
	onClose: PropTypes.func.isRequired,
	onReferVisit: PropTypes.func.isRequired,
	patientId: PropTypes.number.isRequired,
};

export default ReferralModal;
